import { useState, useEffect, useReducer } from 'react';

import ToolTip from 'components/ui-components/ToolTip';
import { v4 as uuidv4 } from 'uuid';
import { useHTTP } from 'hooks/useRequest';
import { screenSize } from 'lib/utils';
import useErrorPush from 'utils/useErrorPush';
import { grey70, grey50 } from 'constants/variables';
import { InfoIcon, CloseWhite } from 'assets/icons';

const initialState = {
  emailError: '',
  telephoneError: '',
  collectionDetailsConfirmed: false,
  collectionInformation: {
    engineers_name: '',
    telephone: '',
    email: '',
    purchase_order: '',
    instructions: '',
  },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_EMAIL_ERROR':
      return {
        ...state,
        emailError: action.payload.emailError,
        collectionInformation: {
          ...state.collectionInformation,
          ...action.payload.updatedField,
        },
      };
    case 'SET_TELEPHONE_ERROR':
      return {
        ...state,
        telephoneError: action.payload.telephoneError,
        collectionInformation: {
          ...state.collectionInformation,
          ...action.payload.updatedField,
        },
      };
    case 'SET_COLLECTION_DETAILS_CONFIRMED':
      return { ...state, collectionDetailsConfirmed: action.payload };
    case 'SET_COLLECTION_INFORMATION': {
      return {
        ...state,
        collectionInformation: {
          ...state.collectionInformation,
          ...action.payload,
        },
      };
    }
    default:
      return state;
  }
};

const NextStep = ({
  orderType,
  state,
  onCompleteOrder,
  onUpdateInformation,
}) => {
  const isDisabled =
    !state.collectionDetailsConfirmed ||
    state.collectionInformation.email === '' ||
    state.emailError ||
    state.collectionInformation.telephone === '';

  if (orderType === 'COLLECTION') {
    return (
      <button
        className="cef-btn cef-btn--green"
        type="button"
        onClick={onCompleteOrder}
        disabled={isDisabled}>
        Place Order
      </button>
    );
  }

  return (
    <button
      type="button"
      className="cef-btn cef-btn--green ff-body"
      onClick={onUpdateInformation}
      disabled={isDisabled}>
      Continue to Payment
    </button>
  );
};

const ShowDisplayError = ({ displayErrors }) => (
  <div className="showPaymentDisplayError">
    <ul className="errors">
      {displayErrors.map(errorMessage => (
        <li key={uuidv4()}>{errorMessage}</li>
      ))}
    </ul>
  </div>
);

const Collection = ({
  accountDetails,
  guestTelephone,
  completeCollectionOnlyOrder: propsCompleteCollectionOnlyOrder,
  setLoadingState,
  showDetailErrors,
  setCurrentStep,
  collectionUrl,
  current_step: currentStep,
  orderType,
  signed_in: signedIn,
  show_display_error: propsShowDisplayError,
  display_errors: displayErrors,
  processingState,
  step,
  collectionAddress,
  splitBasketTelephone,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [requestConfig, setRequestConfig] = useState({
    url: null,
    method: '',
    headers: {
      Accept: 'application/json',
    },
  });

  const { data: collectionProcessedResponse, error } = useHTTP(requestConfig);

  useEffect(() => {
    if (collectionProcessedResponse) {
      showDetailErrors(false);
      setCurrentStep('PAYMENT');
      setLoadingState(false);
      setRequestConfig({
        url: null,
        method: '',
        headers: {
          Accept: 'application/json',
        },
      });
    }
  }, [collectionProcessedResponse]);

  useEffect(() => {
    if (error) {
      showDetailErrors(
        true,
        error.response ? error.response.data : 'Unknown error',
      );
      setLoadingState(false);
      useErrorPush(
        "checkout: There's been an error updating collection information.",
        window.dataLayer,
      );
    }
  }, [error]);

  useEffect(() => {
    const updateAccountDetails = () => {
      const detailsEngineersName = accountDetails.name || '';
      const detailsTelephone =
        accountDetails.telephone ||
        guestTelephone ||
        splitBasketTelephone ||
        '';
      const detailsEmail = accountDetails.email || '';

      dispatch({
        type: 'SET_COLLECTION_INFORMATION',
        payload: {
          telephone: detailsTelephone,
          engineers_name: detailsEngineersName,
          email: detailsEmail,
        },
      });
    };

    updateAccountDetails();
  }, [accountDetails, guestTelephone, splitBasketTelephone]);

  const handleChange = (e, field) => {
    switch (field) {
      case 'email':
        {
          const emailRegex =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

          // Expand the validation
          if (emailRegex.test(e.target.value)) {
            dispatch({
              type: 'SET_EMAIL_ERROR',
              payload: {
                emailError: '',
                updatedField: { [field]: e.target.value },
              },
            });
          } else {
            dispatch({
              type: 'SET_EMAIL_ERROR',
              payload: {
                emailError: 'Please enter a valid email address.',
                updatedField: { [field]: e.target.value },
              },
            });
          }
        }
        break;
      case 'telephone':
        {
          const phoneRegex = /^\d+$/;
          const value = e.target.value;

          if (value.trim() === '') {
            dispatch({
              type: 'SET_TELEPHONE_ERROR',
              payload: {
                telephoneError: 'Telephone number is required.',
                updatedField: { [field]: value },
              },
            });
          } else if (phoneRegex.test(value)) {
            dispatch({
              type: 'SET_TELEPHONE_ERROR',
              payload: {
                telephoneError: '',
                updatedField: { [field]: value },
              },
            });
          } else {
            dispatch({
              type: 'SET_TELEPHONE_ERROR',
              payload: {
                telephoneError: 'Please enter a valid telephone number.',
                updatedField: { [field]: value },
              },
            });
          }
        }
        break;

      default: {
        dispatch({
          type: 'SET_EMAIL_ERROR',
          payload: {
            emailError: '',
            updatedField: { [field]: e.target.value },
          },
        });
      }
    }
  };

  const toggleCollectionDetailsConfirmed = () => {
    dispatch({
      type: 'SET_COLLECTION_DETAILS_CONFIRMED',
      payload: !state.collectionDetailsConfirmed,
    });

    if (!state.collectionInformation.telephone) {
      dispatch({
        type: 'SET_TELEPHONE_ERROR',
        payload: {
          telephoneError: 'Please enter a valid telephone number.',
        },
      });
    }
  };

  const completeCollectionOnlyOrder = () => {
    const { collectionInformation, collectionDetailsConfirmed } = state;

    propsCompleteCollectionOnlyOrder(
      collectionInformation,
      collectionDetailsConfirmed,
    );
  };

  const updateCollectionInformation = () => {
    setRequestConfig({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'put',
      url: collectionUrl,
      data: {
        collection_information: state.collectionInformation,
        collection_confirmed: state.collectionDetailsConfirmed,
      },
    });
    setLoadingState(true);
  };

  const editThisStep = () => {
    setCurrentStep('COLLECTION_DETAILS');
  };

  return (
    <div
      className={`checkout-panel collection-details-step ${
        currentStep === 'COLLECTION_DETAILS' ? '' : 'not-active'
      }${processingState === true ? ' processing-payment' : ''}`}>
      <div className="checkout-header-title">
        <div className="checkout-header-title-left">
          <strong>{step}.</strong> Collection Details
        </div>
        <div className="checkout-header-title-right">
          {currentStep === 'PAYMENT' && orderType === 'MIXED' && (
            <strong
              className="pull-right edit"
              onClick={editThisStep}
              onKeyDown={editThisStep}
              role="button"
              tabIndex="0">
              Edit
            </strong>
          )}
        </div>
      </div>

      <div className="checkout-sub-panel">
        <div>
          <strong>{collectionAddress.name}</strong>
          <br />
          {collectionAddress.line_1}
          <br />
          {collectionAddress.line_2}
          <br />
          {collectionAddress.line_3}
          <br />
          {collectionAddress.line_4}
          <br />
          {collectionAddress.line_5}
          <br />
          {collectionAddress.postcode}
          <hr />
        </div>

        {signedIn && (
          <p>
            <strong>
              Please provide the contact details of the person collecting the
              goods:
            </strong>
          </p>
        )}

        <div className="fl-on fl-jc-sb person-collecting-details">
          <div className="fl-fb-50">
            <label htmlFor="collectionName">
              {signedIn ? "Engineer's Name" : 'Name'}
              <input
                id="collectionName"
                type="text"
                onChange={e => handleChange(e, 'engineers_name')}
                value={state.collectionInformation.engineers_name}
              />
            </label>
          </div>

          <div className="fl-fb-50">
            <label htmlFor="collectionEmail">
              Email <span className="required">*</span>
              <input
                id="collectionEmail"
                type="email"
                onChange={e => handleChange(e, 'email')}
                value={state.collectionInformation.email}
              />
            </label>
            {state.emailError && (
              <p>
                <span className="search-postcode--error">
                  {state.emailError}
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="fl-on fl-jc-sb">
          <div className="fl-fb-50 collection-phone">
            <label htmlFor="collectionTelephone">
              Telephone <span className="required">*</span>
              <input
                type="tel"
                id="collectionTelephone"
                onChange={e => handleChange(e, 'telephone')}
                value={state.collectionInformation.telephone}
              />
            </label>
            {state.telephoneError && (
              <p>
                <span className="search-postcode--error">
                  {state.telephoneError}
                </span>
              </p>
            )}
          </div>
          {orderType === 'COLLECTION' && (
            <div className="fl-fb-50 collection-phone pos-r purchase__order-wrapper">
              <label htmlFor="purchase_order" className="mb-5">
                {signedIn ? 'Purchase Order' : 'Your Reference'}
                {accountDetails.purchase_order_required ? (
                  <span className="required"> *</span>
                ) : null}
                {signedIn && (
                  <ToolTip
                    bgColor={grey70}
                    className="purchase__order-tooltip jc-fs ta-l"
                    toolTipIcon={
                      <InfoIcon width="14" height="14" fillColor={grey50} />
                    }
                    closeIcon={
                      screenSize() < 1025 && (
                        <CloseWhite width="14" height="14" />
                      )
                    }>
                    <p className="tooltip-content ff-body lh-1-3 nm">
                      This is the unique reference assigned to a purchase order.
                      If you do not use purchase orders, simply add text as
                      reference.
                    </p>
                  </ToolTip>
                )}
                <input
                  id="purchase_order"
                  type="text"
                  onChange={e => handleChange(e, 'purchase_order')}
                  value={state.collectionInformation.purchase_order}
                />
              </label>
            </div>
          )}
        </div>

        <div className="collection-instructions">
          <label htmlFor="collectionInstructions">
            Instructions
            <textarea
              id="collectionInstructions"
              onChange={e => handleChange(e, 'instructions')}
              value={state.collectionInformation.instructions}
            />
          </label>
        </div>
        <div className="collection-confirmation">
          <div className="collection-notice">
            {signedIn ? (
              <label
                htmlFor="collectionDetailsConfirmed"
                className="fl ai-fs mb-0">
                <input
                  id="collectionDetailsConfirmed"
                  type="checkbox"
                  checked={state.collectionDetailsConfirmed}
                  onChange={toggleCollectionDetailsConfirmed}
                />
                <span>
                  I am aware that my click and collect items will be invoiced on
                  my CEF Trade Account or paid for by Debit or Credit Card at
                  the time of collection. Please wait for you order to be
                  confirmed before you leave to collect your goods. You will
                  receive an email when your goods are ready.
                </span>
              </label>
            ) : (
              <label htmlFor="collectionDetailsConfirmed" className="fl ai-fs">
                <input
                  id="collectionDetailsConfirmed"
                  type="checkbox"
                  checked={state.collectionDetailsConfirmed}
                  onChange={toggleCollectionDetailsConfirmed}
                />
                <span>
                  I am aware that my click and collect items will be paid for at
                  the time of collection. Please wait for your order to be
                  confirmed before you leave to collect your goods. You will
                  receive an email when your goods are ready.
                </span>
              </label>
            )}
          </div>
        </div>
        {propsShowDisplayError && (
          <ShowDisplayError displayErrors={displayErrors} />
        )}
      </div>
      <div className="checkout-sub-panel fl-on fl-sm-on next-step">
        <NextStep
          orderType={orderType}
          state={state}
          onCompleteOrder={completeCollectionOnlyOrder}
          onUpdateInformation={updateCollectionInformation}
        />
      </div>
    </div>
  );
};

export default Collection;
